import type { Cut, BibCut, HeightBand } from '@/types/quiz'

export interface ProductImage {
  /** Shopify CDN URL. Null when no image has been uploaded yet - UI falls back to a grey placeholder. */
  url: string | null
  /** Alt text for screen readers. Kept human and brand-voiced rather than copying Shopify's auto-generated alt. */
  alt: string
  /** Intrinsic image dimensions used for layout aspect ratio. Same value used for placeholder fallback. */
  width: number
  height: number
  /** Shown inside the grey-box placeholder when url is null. */
  placeholderLabel: string
}

export interface ProductStub {
  /** Product handle per length - each length is its own Shopify product. */
  handles: Record<HeightBand, string>
  /** Display name - always use "our [Name]" when referring to it in body copy. */
  name: string
  /** Display price. Refresh with scripts/refresh-product-data.mjs if Shopify prices change. */
  price: string
  /** Featured image. Real URL when present in Shopify, null falls back to placeholder. */
  image: ProductImage
  /** Short one-liner. */
  tagline: string
  /**
   * Pre-launch flag. When present, the ProductCard renders an "Arriving …"
   * chip over the image, swaps the CTA copy to "Learn more" (pointing at the
   * PDP where the customer can sign up to be notified), and the result
   * screen shows an available-now alternative card beneath.
   *
   * Remove this field on launch day - the UI will automatically revert to
   * the normal Choose Options CTA.
   */
  comingSoon?: {
    /** Chip copy, e.g. "Arriving June 2026". Kept flexible so the wording stays under Maria's control. */
    label: string
  }
}

/**
 * Pant products keyed by cut -> length -> handle. Each cut has its own
 * Shopify product per length. Beatrice has Short and Regular only - no
 * Tall product (the scoring module converts Beatrice+tall to Mila+tall
 * before this data is read).
 *
 * Image URLs are pulled from Shopify featured images. To refresh after
 * Maria swaps product photography, run:
 *   SHOPIFY_ACCESS_TOKEN=$(netlify env:get SHOPIFY_ACCESS_TOKEN) \
 *   SHOPIFY_DOMAIN=$(netlify env:get SHOPIFY_DOMAIN) \
 *     node scripts/refresh-product-data.mjs
 */
export const PANTS: Record<Cut, ProductStub> = {
  zali: {
    handles: {
      short:   'zali-snow-pant-short',
      regular: 'zali-snow-pant-regular',
      tall:    'zali-snow-pant-tall',
    },
    name: 'Zali Snow Pant',
    price: '$329 AUD',
    image: {
      url: 'https://cdn.shopify.com/s/files/1/0477/4874/3333/files/zali-snow-pant-regular-190979.webp?v=1768291162',
      alt: 'Zali Snow Pant',
      width: 1200,
      height: 1500,
      placeholderLabel: 'Zali pant',
    },
    tagline: 'Built for fuller hips, bum, and thighs - with a waist that actually fits.',
  },
  mila: {
    handles: {
      short:   'mila-snow-pant-short',
      regular: 'mila-snow-pant-regular',
      tall:    'mila-snow-pant-tall',
    },
    name: 'Mila Snow Pant',
    price: '$329 AUD',
    image: {
      url: 'https://cdn.shopify.com/s/files/1/0477/4874/3333/files/Mila_AU24_Plum_Regular_2.webp?v=1749801144',
      alt: 'Mila Snow Pant',
      width: 1200,
      height: 1500,
      placeholderLabel: 'Mila pant',
    },
    tagline: 'Subtly contoured through the waist and hip - gentle curve, generous movement.',
  },
  beatrice: {
    handles: {
      short:   'beatrice-snow-pant-short',
      regular: 'beatrice-snow-pant-regular',
      // Defensive - scoring converts Beatrice+tall to Mila+tall before this
      // is read. Pointing at -regular instead of a non-existent -tall handle
      // means a regression fails-soft to a real product page rather than 404.
      tall:    'beatrice-snow-pant-regular',
    },
    name: 'Beatrice Snow Pant',
    price: '$329 AUD',
    image: {
      url: 'https://cdn.shopify.com/s/files/1/0477/4874/3333/files/beatrice-pant-black-regular-sam-size202x-front-handsdown.webp?v=1776673217',
      alt: 'Beatrice Snow Pant',
      width: 1200,
      height: 1500,
      placeholderLabel: 'Beatrice pant',
    },
    tagline: 'Generous through the middle, contoured through the leg - room where you need it.',
  },
}

/**
 * Bib products keyed by cut -> length. Only Zali and Mila have bibs.
 * Beatrice winners fall back to the Mila bib (handled by the scoring module).
 */
export const BIBS: Record<BibCut, ProductStub> = {
  zali: {
    handles: {
      short:   'zali-snow-bib-short',
      regular: 'zali-snow-bib-regular',
      tall:    'zali-snow-bib-tall',
    },
    name: 'Zali Snow Bib',
    price: '$449 AUD',
    image: {
      url: 'https://cdn.shopify.com/s/files/1/0477/4874/3333/files/Zali_AU14_Sliva_Regular_8.webp?v=1768290970',
      alt: 'Zali Snow Bib',
      width: 1200,
      height: 1500,
      placeholderLabel: 'Zali bib',
    },
    tagline: 'Bib coverage with our Zali-style fit - same curves, same adjustability.',
  },
  mila: {
    handles: {
      short:   'mila-snow-bib-short',
      regular: 'mila-snow-bib-regular',
      tall:    'mila-snow-bib-tall',
    },
    name: 'Mila Snow Bib',
    price: '$449 AUD',
    image: {
      url: 'https://cdn.shopify.com/s/files/1/0477/4874/3333/files/Mila_AU16_Moss_Regular_1.webp?v=1768290809',
      alt: 'Mila Snow Bib',
      width: 1200,
      height: 1500,
      placeholderLabel: 'Mila bib',
    },
    tagline: 'Snow bib for a straighter, or subtly curved shape - or for Beatrice matches who want bib coverage.',
  },
}
